import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  MessageSquare, 
  Target, 
  Share2, 
  FileCheck, 
  TrendingUp, 
  ArrowRight,
  CheckCircle,
  PhoneCall
} from 'lucide-react';
import './CrmSoftware.css';
import FAQAccordion from './FAQAccordion';

const crmFeatures = [
  {
    icon: <MessageSquare size={24} />,
    title: 'WhatsApp Automation & Chatbot',
    desc: 'Automate instant customer lead replies, broadcast seasonal promotional campaigns, send GST quotation PDFs, and manage support chats directly via official WhatsApp Cloud API.'
  },
  {
    icon: <Share2 size={24} />,
    title: 'IndiaMart & Justdial Lead Auto-Sync',
    desc: 'Never lose a hot lead. Auto-capture incoming inquiries in real time from IndiaMart, Justdial, Facebook Ads, Google Ads, and website contact forms into a unified pipeline.'
  },
  {
    icon: <Target size={24} />,
    title: 'Visual Sales Pipeline & Call Follow-ups',
    desc: 'Drag-and-drop deal stages, automated agent task assignments, scheduled call reminders, call recording links, and deal aging alerts to prevent lead drop-offs.'
  },
  {
    icon: <FileCheck size={24} />,
    title: 'One-Click GST Quotations & Proforma Invoices',
    desc: 'Generate branded PDF quotations with multi-slab GST calculation, payment terms, and convert accepted quotes into Tally-synced sales invoices instantly.'
  },
  {
    icon: <Users size={24} />,
    title: 'Dealer, Distributor & Agent Portal',
    desc: 'Custom portal for distributors, dealers, and field agents to submit bulk purchase orders, track dispatch status, and view commission ledgers in real time.'
  },
  {
    icon: <TrendingUp size={24} />,
    title: 'Sales Rep Leaderboards & Revenue Forecasts',
    desc: 'Track sales rep daily call activity, conversion velocity, monthly revenue targets, and lost deal reasons through intuitive executive dashboards.'
  }
];

const crmProcessDetailed = [
  { num: '01', title: 'Sales Funnel & Lead Audit', desc: 'Analyzing your inbound lead channels (IndiaMart, Justdial, website forms), sales team structure, and quotation templates.' },
  { num: '02', title: 'Custom Pipeline & Roles Design', desc: 'Configuring custom pipeline stages, commission structures, WhatsApp automated auto-replies, and access levels.' },
  { num: '03', title: 'WhatsApp API & Lead Webhooks Sync', desc: 'Connecting official WhatsApp Cloud API, IndiaMart webhooks, Justdial buyer API, and Tally ledgers.' },
  { num: '04', title: 'Mobile App Setup for Sales Reps', desc: 'Setting up Android & iOS mobile app access for your sales reps to log calls, update deals, and generate quotes on the move.' },
  { num: '05', title: 'Sales Team Onboarding & Trial', desc: 'Conducting live onboarding sessions for sales managers and sales reps to ensure smooth adoption.' },
  { num: '06', title: 'Continuous AMC & Analytics Support', desc: 'Providing ongoing technical support, monthly sales performance analytics reports, and feature expansions.' }
];

const crmIntegrations = [
  'WhatsApp Cloud API', 'IndiaMart Lead API', 'Justdial Buyer API',
  'Tally Prime Sync', 'Razorpay Payment Links', 'Google Calendar Sync',
  'Facebook Lead Ads', 'Website Form Webhooks', 'Email SMTP Broadcast',
  'Twilio / Exotel Click-to-Call', 'Custom REST APIs', 'Excel / CSV Import & Export'
];

const crmFaqsDetailed = [
  { q: 'How is custom CRM implementation done step-by-step?', a: 'Our onboarding follows 6 stages: (1) Lead Channel Audit, (2) Pipeline & Role Customization, (3) WhatsApp API & Webhook Integration, (4) Sales Mobile App Setup, (5) Sales Team Onboarding, and (6) Ongoing AMC Support.' },
  { q: 'Can this custom CRM capture leads from IndiaMart and Justdial automatically?', a: 'Yes! We integrate directly with IndiaMart and Justdial buyer APIs to push every inquiry instantly into your sales pipeline within seconds.' },
  { q: 'Does the WhatsApp feature use the official WhatsApp Business API?', a: 'Yes, we use the official WhatsApp Cloud API, allowing your sales team to send approved message templates, PDF quotations, and automated replies safely without getting banned.' },
  { q: 'Can we customize deal stages and user roles for our sales team?', a: 'Absoltely. Unlike rigid SaaS tools, we customize the CRM pipeline stages, user permission levels, commission rules, and reports to fit your exact sales process.' },
  { q: 'Can our sales reps use the CRM on their Android or iPhone mobile devices?', a: 'Yes! We provide dedicated Android and iOS mobile app access so your field agents can log calls, update lead status, and generate quotes on the go.' },
  { q: 'Can quotes be exported directly as PDF and sent over WhatsApp?', a: 'Yes! Your sales team can generate a branded GST quotation PDF in one click and send it directly to the prospect\'s WhatsApp inbox.' },
  { q: 'Do we get full source code ownership of the CRM?', a: 'Yes! 100% of the custom CRM source code, database, and repository belong to your company upon launch.' }
];

export default function CrmSoftware() {
  const whatsappUrl = "https://wa.me/918287999236?text=Hi%20Wayug!%20I%20would%20like%20a%20demo%20of%20your%20Custom%20CRM%20Software.";

  return (
    <section className="crm-section">
      <div className="container">
        
        {/* Hero */}
        <motion.div 
          className="crm-hero"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="crm-badge">
            <Users size={16} /> Custom CRM &amp; Software Development
          </div>
          <h1 className="crm-title">
            Custom CRM Development for <span className="gradient-text">Clients &amp; Businesses</span>
          </h1>
          <p className="crm-subtitle">
            We build custom CRMs, websites, mobile apps, and bespoke software solutions designed specifically around your client workflows, sales pipelines, WhatsApp messaging, IndiaMart lead capture, and business processes.
          </p>
          <div style={{ marginTop: '2rem', display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href={whatsappUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              Discuss Your Custom CRM Project <ArrowRight size={16} />
            </a>
            <a href="/contact" className="btn btn-outline">
              Get Custom Proposal
            </a>
          </div>
        </motion.div>

        {/* Feature Grid */}
        <div className="crm-features-grid">
          {crmFeatures.map((item, idx) => (
            <motion.div 
              key={item.title}
              className="crm-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
            >
              <div className="crm-card-icon">{item.icon}</div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* 6-Step Onboarding Process */}
        <div style={{ margin: '80px 0' }}>
          <h2 className="text-center" style={{ fontSize: '2.2rem', fontWeight: '800', marginBottom: '12px' }}>
            How Custom CRM Onboarding Is Done: <span className="gradient-text">Our 6-Step Process</span>
          </h2>
          <p className="text-center" style={{ color: '#a3a3a3', maxWidth: '700px', margin: '0 auto 40px', fontSize: '1rem', lineHeight: '1.6' }}>
            Here is how we customize and deploy your sales pipeline with zero friction.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {crmProcessDetailed.map((step) => (
              <div key={step.num} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '28px' }}>
                <span style={{ fontSize: '2rem', fontWeight: '800', color: '#a78bfa' }}>{step.num}</span>
                <h4 style={{ fontSize: '1.2rem', fontWeight: '700', margin: '12px 0 8px', color: '#fff' }}>{step.title}</h4>
                <p style={{ color: '#a3a3a3', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Integrations Grid */}
        <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '40px', margin: '60px 0' }}>
          <h3 style={{ fontSize: '1.8rem', fontWeight: '800', textAlign: 'center', marginBottom: '24px' }}>
            Pre-Built CRM Integrations Included
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            {crmIntegrations.map((i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#e5e5e5', fontSize: '0.95rem' }}>
                <CheckCircle size={16} color="#a78bfa" /> {i}
              </div>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <FAQAccordion 
          title="Detailed Custom CRM FAQs" 
          faqs={crmFaqsDetailed} 
          accentColor="#a78bfa" 
        />

        {/* Bottom High-Converting Contact Banner */}
        <div style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.15) 0%, rgba(37,99,235,0.15) 100%)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: '24px', padding: '48px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '800', color: '#fff', marginBottom: '12px' }}>Empower Your Sales Team Today</h2>
          <p style={{ color: '#a3a3a3', maxWidth: '600px', margin: '0 auto 24px', fontSize: '1rem', lineHeight: '1.6' }}>
            Stop lead leakage and automate WhatsApp follow-ups. Schedule a live custom CRM demonstration today.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <MessageSquare size={16} /> Chat on WhatsApp (+91 8287999236)
            </a>
            <a href="/contact" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <PhoneCall size={16} /> Request CRM Demo
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
